import { Icon } from '@iconify/react';
import { forwardRef, type HTMLAttributes } from 'react';

type Tone = 'danger' | 'success' | 'warning' | 'info';

const tones: Record<Tone, { wrap: string; icon: string }> = {
  danger: {
    wrap: 'bg-[var(--color-danger-subtle)] border-[var(--color-danger)]/20 text-[var(--color-danger)]',
    icon: 'heroicons:exclamation-triangle',
  },
  success: {
    wrap: 'bg-[var(--color-success-subtle)] border-[var(--color-success)]/20 text-[var(--color-success)]',
    icon: 'heroicons:check-circle',
  },
  warning: {
    wrap: 'bg-[var(--color-warning-subtle)] border-[var(--color-warning)]/20 text-[var(--color-warning)]',
    icon: 'heroicons:exclamation-circle',
  },
  info: {
    wrap: 'bg-[var(--color-accent-subtle)] border-[var(--color-accent)]/20 text-[var(--color-accent)]',
    icon: 'heroicons:information-circle',
  },
};

export interface AlertProps extends HTMLAttributes<HTMLDivElement> {
  tone?: Tone;
}

/**
 * Inline alert banner with a leading status icon. Defaults to the danger tone.
 * Forwards its ref so a form can move focus to it (`tabIndex={-1}`) after an error.
 */
export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { tone = 'danger', className = '', children, ...rest },
  ref,
) {
  const t = tones[tone];
  return (
    <div
      ref={ref}
      role="alert"
      className={['flex items-start gap-x-2 rounded-lg border p-3.5 text-sm', t.wrap, className]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <Icon icon={t.icon} className="h-5 w-5 shrink-0 mt-px" aria-hidden="true" />
      <div className="min-w-0">{children}</div>
    </div>
  );
});
